import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getInvoice, getCompany, sendInvoice, updateInvoice, deleteInvoice } from '@/lib/api';
import { useAuth } from '@/components/AuthProvider';
import { showNotification } from '@/lib/generalNotifications';
import { defaultTemplates } from '@/lib/emailTemplates';
import { 
  CheckCircle, 
  AlertCircle, 
  ChevronLeft, 
  Send, 
  Mail, 
  Loader2,
  ArrowRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  AlignLeft,
  FileText,
  Users,
  Save
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { collection, addDoc, doc, getDoc, updateDoc, query, getDocs, where, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { LazyEmailEditor as EmailEditor } from '@/components/LazyEmailEditor';
import { generateInvoiceNumber, generatePaymentKey, generateRecurringTemplateNumber } from '@/lib/utils';
import { Invoice } from '../types/firebase';
import { addInvoiceToTemplateTracking } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

// +++ Add Helper Functions +++
const formatDate = (dateValue: any): string => {
  if (!dateValue) return '';
  
  try {
    let date;
    // Handle Firestore Timestamp objects
    if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
      date = new Date(dateValue.seconds * 1000);
    } else if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      // Handle YYYY-MM-DD format specifically to avoid timezone issues
      const [year, month, day] = dateValue.split('-').map(Number);
      date = new Date(year, month - 1, day); // month is 0-indexed
    } else {
      // Handle standard Date objects or ISO strings
      date = new Date(dateValue);
    }
    
    
    // Get local date components without timezone conversion
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    
    // Create a new date using local components to avoid timezone shift
    const localDate = new Date(year, month, day);
    
    
    // Use locale-specific date format to display the date
    return localDate.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

const extractFirstName = (fullName: string): string => {
  if (!fullName) return '';
  return fullName.split(' ')[0];
};
// +++ End Helper Functions +++

// +++ Add template processing logic (Moved Before useEffect) +++
const processEmailTemplate = (htmlContent: string, currentInvoice: any, currentCompany: any): string => {
  if (!currentInvoice || !currentCompany) return htmlContent; // Return raw if data missing


  const firstName = extractFirstName(currentInvoice.customerName);
  // Compute the real payment URL
  const paymentDomain = 'https://www.secured-payment-processing.com';
  const paymentUrl = `${paymentDomain}/pay/${currentInvoice.id}`;

  let processedContent = htmlContent
    .replace(/\{\{firstName\}\}/g, firstName || '')
    .replace(/\{\{customerName\}\}/g, currentInvoice.customerName || '')
    .replace(/\{\{invoiceNumber\}\}/g, currentInvoice.invoiceNumber || '')
    .replace(/\{\{invoiceAmount\}\}/g, `$${((currentInvoice.subtotal || 0) + (currentInvoice.taxAmount || 0)).toFixed(2)}`)
    .replace(/\{\{dueDate\}\}/g, formatDate(currentInvoice.dueDate))
    .replace(/\{\{companyName\}\}/g, currentCompany.name || 'Your Company')
    .replace(/\{\{frequency\}\}/g, currentInvoice.recurringDetails?.frequency ? 
      (currentInvoice.recurringDetails.frequency === 'weekly' ? 'Weekly' :
       currentInvoice.recurringDetails.frequency === 'monthly' ? 'Monthly' :
       currentInvoice.recurringDetails.frequency === 'quarterly' ? 'Quarterly' : 'Annual') : 'Monthly');

  // Replace payment button with correct URL
  const paymentButtonHtml = `
    <div data-type="payment-button" class="payment-button-wrapper" style="text-align: center; margin: 30px 0;">
      <a href="${paymentUrl}" 
         target="_blank" 
         rel="noopener noreferrer nofollow" 
         class="payment-button" 
         style="display: inline-block; background-color: #4ADE80; color: white; padding: 12px 32px; text-decoration: none; border-radius: 6px; font-weight: 500; font-size: 18px; border: none; text-align: center; min-width: 240px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
        View & Pay Invoice
      </a>
    </div>
  `;

  // Replace any existing payment button or {{paymentLink}} placeholder
  processedContent = processedContent.replace(/<div[^>]*data-type="payment-button"[^>]*>.*?<\/div>/gs, paymentButtonHtml);
  processedContent = processedContent.replace(/\{\{paymentLink(:.*?)?\}\}/g, paymentButtonHtml);

  return processedContent;
};

const processSubjectTemplate = (subjectTemplate: string, currentInvoice: any, currentCompany: any): string => {
  if (!currentInvoice || !currentCompany) return subjectTemplate;
  const firstName = extractFirstName(currentInvoice.customerName);
  return subjectTemplate
    .replace(/\{\{firstName\}\}/g, firstName || '')
    .replace(/\{\{customerName\}\}/g, currentInvoice.customerName || '')
    .replace(/\{\{invoiceNumber\}\}/g, currentInvoice.invoiceNumber || '')
    .replace(/\{\{companyName\}\}/g, currentCompany.name || 'Your Company');
};

// After processing the currentBodyHtml, ensure {{paymentLink}} is present
const ensurePaymentLink = (body: string) => {
  // If the template already has a payment link, do nothing
  if (body.includes('{{paymentLink')) return body;
  // Otherwise, append it at the end with a line break
  return `${body}<br/><br/>{{paymentLink}}`;
};
// +++ End template processing logic +++

export default function InvoiceEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Get invoice ID from URL params
  const invoiceId = searchParams.get('id');
  const [retryCount, setRetryCount] = useState(0);
  
  // Fetch invoice data with retry capability
  const { data: invoice, isLoading: isLoadingInvoice, refetch: refetchInvoice } = useQuery({
    queryKey: ['invoice', invoiceId, user?.companyId],
    queryFn: async () => {
      if (!invoiceId) return null;
      
      // First try to get from regular invoices collection
      const regularInvoice = await getInvoice(invoiceId);
      if (regularInvoice) {
        return regularInvoice;
      }
      
      // If not found, check in the templates collection
      try {
        const templateRef = doc(db, 'invoiceTemplates', invoiceId);
        const templateSnap = await getDoc(templateRef);
        
        if (templateSnap.exists()) {
          const templateData = templateSnap.data();
          
          // Convert template format to invoice format for compatibility
          return {
            id: templateSnap.id,
            invoiceNumber: templateData.invoiceNumber,
            customerName: templateData.customerName,
            customerEmail: templateData.customerEmail,
            customerId: templateData.customerDetails?.id,
            customerCompanyName: templateData.customerCompanyName,
            invoiceType: 'recurring',
            status: templateData.status || 'active',
            date: templateData.createdAt ? new Date(templateData.createdAt.toDate()).toISOString() : new Date().toISOString(),
            dueDate: templateData.dueDate || new Date().toISOString(),
            items: templateData.lineItems || [],
            subtotal: templateData.invoiceDetails?.subtotal || 0,
            taxRate: templateData.invoiceDetails?.taxRate || 0,
            taxAmount: templateData.invoiceDetails?.taxAmount || 0,
            platformFee: templateData.invoiceDetails?.platformFee || 0,
            total: templateData.invoiceDetails?.total || 0,
            notes: templateData.invoiceDetails?.notes || '',
            recurringDetails: templateData.recurringDetails || {},
            // Use template's email template if available
            emailTemplate: templateData.emailTemplate,
            // Add missing properties to fix TypeScript errors
            companyId: templateData.companyId || '',
            customerAddress: templateData.customerDetails?.address || '',
            street: templateData.customerDetails?.street || '',
            city: templateData.customerDetails?.city || '',
            state: templateData.customerDetails?.state || '',
            zip: templateData.customerDetails?.zip || '',
            // Add other required fields with reasonable defaults
            paymentMethods: templateData.paymentMethods || ['credit_card'],
            activities: [],
            footerText: templateData.footerText || '',
            paymentKey: templateData.paymentKey || `pay_${Date.now()}`
          };
        }
        
        return null;
      } catch (err) {
        console.error('Error fetching from templates collection:', err);
        return null;
      }
    },
    enabled: !!invoiceId && !!user?.companyId,
    retry: 3,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
  
  // Retry logic for when invoice isn't found initially
  useEffect(() => {
    if (!isLoadingInvoice && !invoice && retryCount < 3) {
      // Wait a bit and try again
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        refetchInvoice();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [invoice, isLoadingInvoice, retryCount, refetchInvoice]);
  
  // Fetch company settings
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', user?.companyId],
    queryFn: () => user?.companyId ? getCompany(user.companyId) : null,
    enabled: !!user?.companyId,
  });
  
  // Set up mutation for sending invoice
  const sendInvoiceMutation = useMutation({
    mutationFn: (data: { invoiceId: string; emailSubject: string; emailBody: string }) => 
      sendInvoice(data.invoiceId, data.emailSubject, data.emailBody),
    onSuccess: () => {
      setSending(false);
      setSuccess(true);
      showNotification({
        title: "Success",
        message: "Invoice sent successfully!",
        type: "success"
      });
      
      // Redirect back to invoices list after 2 seconds
      setTimeout(() => {
        navigate('/app/invoices');
      }, 2000);
    },
    onError: (error) => {
      setSending(false);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to send invoice",
        type: "error"
      });
    }
  });
  
  // Handle sending the invoice
  const handleSendInvoice = async () => {
    if (!invoiceId) return;
    if (!emailSubject || !emailBody) return;

    setSending(true);
    sendInvoiceMutation.mutate({ invoiceId, emailSubject, emailBody });
  };
  
  // Handle saving as draft
  const handleSaveAsDraft = async () => {
    if (!invoiceId) return;
    
    // Don't allow saving recurring invoices as draft - they have different handling
    if (invoice?.invoiceType === 'recurring') {
      showNotification({
        title: "Not Available",
        message: "Recurring invoices cannot be saved as draft. Please use the template activation flow instead.",
        type: "warning"
      });
      return;
    }
    
    try {
      setSending(true);
      
      
      // Update the invoice status to draft
      const updatedInvoice = await updateInvoice(invoiceId, { status: 'draft' });
      
      
      showNotification({
        title: "Success",
        message: "Invoice saved as draft successfully!",
        type: "success"
      });
      
      // Redirect back to invoices list after a short delay
      setTimeout(() => {
        navigate('/app/invoices');
      }, 1500);
    } catch (error) {
      console.error('Error saving as draft:', error);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to save invoice as draft",
        type: "error"
      });
    } finally {
      setSending(false);
    }
  };
  
  // Handle back button
  const handleBack = () => {
    navigate(`/app/invoices/preview?id=${invoiceId}`);
  };
  
  // Error state if invoice or company not found
  // Add state outside conditional to avoid hooks ordering issues
  const [showError, setShowError] = useState(false);
  
  useEffect(() => {
    // Only run this effect when invoice/company is not found
    if (!invoice && !company && !isLoadingInvoice && !isLoadingCompany) {
      // Only show error after a short delay to prevent flashing during normal loading
      const timer = setTimeout(() => {
        setShowError(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [invoice, company, isLoadingInvoice, isLoadingCompany]);
  
  const [initialEmailSubject, setInitialEmailSubject] = useState('');
  const [initialEmailBody, setInitialEmailBody] = useState('');
  
  // Handle saving the email template for recurring invoices instead of sending
  const handleSaveRecurringTemplate = async () => {
    try {
      // Defensive checks for user and invoice
      if (!user || !user.email) {
        const error = new Error('User is not loaded');
        console.error('Failed to save recurring template:', error);
        showNotification({ title: "Error", message: "User is not loaded. Please wait and try again.", type: "error" });
        return;
      }
      if (!invoice) {
        const error = new Error('Invoice is not loaded');
        console.error('Failed to save recurring template:', error);
        showNotification({ title: "Error", message: "Invoice is not loaded. Please wait and try again.", type: "error" });
        return;
      }

      setSending(true);
      
      try {
        // Check if this is an existing template being updated
        const isExistingTemplate = invoice?.invoiceType === 'recurring' && invoiceId;
        let templateId = isExistingTemplate ? invoiceId : null;
        let templateData: any = null;
        let createdTemplate: any = null;
        let templateDueDate: Date | null = null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        

        // Save email template if it was modified (for both new and existing templates)
        const shouldSaveEmailTemplate = invoice.invoiceType === 'recurring' && (emailSubject !== initialEmailSubject || emailBody !== initialEmailBody);
        const emailTemplateData = shouldSaveEmailTemplate ? {
          subject: emailSubject,
          body: emailBody
        } : undefined;

        if (isExistingTemplate) {
          // Update the existing template
          const templateRef = doc(db, 'invoiceTemplates', invoice.id);
          const updateData: any = {
            status: 'active',
            'recurringDetails.isActive': true,
            hasBeenActivated: true,
            updatedAt: new Date()
          };
          
          // Only include email template if it was modified and should be saved
          if (emailTemplateData) {
            updateData.emailTemplate = emailTemplateData;
          }
          
          await updateDoc(templateRef, updateData);
          
          // Fetch the updated template data to ensure we have the latest version
          const updatedTemplateSnap = await getDoc(templateRef);
          templateData = updatedTemplateSnap.exists() ? { id: updatedTemplateSnap.id, ...updatedTemplateSnap.data() } : null;
          
          templateId = invoice.id;
          templateDueDate = invoice.dueDate ? new Date(invoice.dueDate) : null;
        } else {
          // Get existing templates to generate a new template number
          const existingTemplatesQuery = query(
            collection(db, 'invoiceTemplates'),
            where('companyId', '==', invoice.companyId)
          );
          const existingTemplatesSnapshot = await getDocs(existingTemplatesQuery);
          const existingTemplates = existingTemplatesSnapshot.docs.map(doc => doc.data());

          // Generate a new template number using the REC-X format
          const newTemplateNumber = generateRecurringTemplateNumber(existingTemplates);

          // Prepare template data with active status
          templateData = {
            baseInvoiceId: invoiceId,
            companyId: invoice.companyId,
            customerName: invoice.customerName,
            customerEmail: invoice.customerEmail,
            customerDetails: {
              id: invoice.customerId,
              name: invoice.customerName,
              email: invoice.customerEmail,
              address: invoice.customerAddress,
              street: invoice.street,
              city: invoice.city,
              state: invoice.state,
              zip: invoice.zip
            },
            invoiceNumber: newTemplateNumber,
            invoiceDetails: {
              subtotal: invoice.subtotal,
              taxRate: invoice.taxRate,
              taxAmount: invoice.taxAmount,
              platformFee: invoice.platformFee,
              total: invoice.total,
              notes: invoice.notes || ''
            },
            // Only include email template if it was modified and should be saved
            ...(emailTemplateData && { emailTemplate: emailTemplateData }),
            lineItems: invoice.items || [],
            recurringDetails: {
              ...(invoice.recurringDetails || {
                frequency: 'monthly'
              }),
              isActive: true
            },
            status: 'active',
            hasBeenActivated: true,
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: user?.email || 'system',
            dueDate: invoice.dueDate,
            // Preserve the schedulePreview from the original invoice
            schedulePreview: (invoice as Invoice).schedulePreview
          };

          // Create a new document in invoiceTemplates collection
          const docRef = await addDoc(collection(db, 'invoiceTemplates'), templateData);
          templateId = docRef.id;
          
          // Fetch the newly created template to ensure we have the complete data
          const newTemplateSnap = await getDoc(docRef);
          templateData = newTemplateSnap.exists() ? { id: newTemplateSnap.id, ...newTemplateSnap.data() } : null;
          
          createdTemplate = templateData;
          templateDueDate = invoice.dueDate ? new Date(invoice.dueDate) : null;

          // Optionally delete the original draft invoice
          if (invoice.status === 'draft') {
            try {
              await deleteInvoice(invoiceId);
            } catch (error) {
              // Silently ignore errors when deleting test invoices
              console.warn('Failed to delete test invoice:', error);
            }
          }
        }

        // --- NEW LOGIC: If the template's first invoice date is today, create a pending invoice ---

        if (templateData?.schedulePreview?.firstInvoiceDate) {
          // Parse firstInvoiceDate and ensure it's in local timezone
          const firstInvoiceDate = new Date(templateData.schedulePreview.firstInvoiceDate);
          const firstInvoiceDateLocal = new Date(
            firstInvoiceDate.getFullYear(),
            firstInvoiceDate.getMonth(),
            firstInvoiceDate.getDate()
          );
          firstInvoiceDateLocal.setHours(0, 0, 0, 0);

          // Get today's date in local timezone
          const today = new Date();
          const todayLocal = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );
          todayLocal.setHours(0, 0, 0, 0);


          // Compare dates - generate invoice if firstInvoiceDate is today or earlier
          if (firstInvoiceDateLocal.getTime() <= todayLocal.getTime()) {
            
            await createStandardInvoiceFromTemplate(
              templateData,
              templateId,
              templateData.schedulePreview.firstInvoiceDate,
              emailTemplateData
            );
          }
          // No action needed if the first invoice date is in the future
        }
        // No action needed if no first invoice date is set
        // --- END NEW LOGIC ---

        // Update the template with the next generation date
        if (templateData.schedulePreview?.secondInvoiceDate) {
          // Convert secondInvoiceDate to start of day in local timezone
          const secondInvoiceDateCopy = new Date(templateData.schedulePreview.secondInvoiceDate);
          // Extract year, month, day and create new date to avoid timezone issues
          const year = secondInvoiceDateCopy.getFullYear();
          const month = secondInvoiceDateCopy.getMonth();
          const day = secondInvoiceDateCopy.getDate();
          const localSecondInvoiceDate = new Date(year, month, day);
          localSecondInvoiceDate.setHours(0, 0, 0, 0);

          // If the second invoice date is in the past, use today's date
          const effectiveNextDate = localSecondInvoiceDate < today ? today : localSecondInvoiceDate;

          // Get the next payment due date (which is typically after the invoice generation date)
          // We'll extract this from the schedulePreview.secondPaymentDue if available
          let nextPaymentDueDate = null;
          if (templateData.schedulePreview?.secondPaymentDue) {
            const secondPaymentDueCopy = new Date(templateData.schedulePreview.secondPaymentDue);
            const paymentDueYear = secondPaymentDueCopy.getFullYear();
            const paymentDueMonth = secondPaymentDueCopy.getMonth();
            const paymentDueDay = secondPaymentDueCopy.getDate();
            nextPaymentDueDate = new Date(paymentDueYear, paymentDueMonth, paymentDueDay);
            nextPaymentDueDate.setHours(0, 0, 0, 0);
          } else {
            // If no specific payment due date is provided, calculate based on payment terms
            // Default to effectiveNextDate + 14 days if no other info available
            nextPaymentDueDate = new Date(effectiveNextDate);
            nextPaymentDueDate.setDate(nextPaymentDueDate.getDate() + 14);
          }

          // Update the template with both the effective next generation date and next payment due date
          await updateDoc(doc(db, 'invoiceTemplates', templateId), {
            nextGenerationDate: effectiveNextDate,
            nextPaymentDueDate: nextPaymentDueDate,
            status: 'active'
          });
          
        }

        setSending(false);
        setSuccess(true);
        showNotification({
          title: "Success",
          message: "Recurring invoice template saved and activated successfully!",
          type: "success"
        });
        setTimeout(() => {
          navigate('/app/invoices?tab=recurring');
        }, 2000);
      } catch (innerError) {
        console.error('Error in template processing:', innerError);
        throw innerError;
      }
    } catch (error) {
      console.error('Error saving recurring template:', error);
      if (error instanceof Error) {
        showNotification({
          title: "Error Saving Template",
          message: error.message || "An unexpected error occurred",
          type: "error"
        });
      } else {
        console.error('Unknown error type:', error);
        showNotification({
          title: "Error",
          message: "An unexpected error occurred while saving the template",
          type: "error"
        });
      }
    } finally {
      setSending(false);
    }
  };
  
  // Check if the current invoice is a recurring type
  const isRecurringInvoice = invoice?.invoiceType === 'recurring';
  
  // After fetching invoice and company, set initial email subject/body from company template
  useEffect(() => {

    // Loading guard: only proceed if both invoice and company are loaded
    if (!invoice || !company || isLoadingCompany || isLoadingInvoice) {
      return;
    }

    const invoiceType = invoice.invoiceType || 'standard';
    
    let currentSubject = '';
    let currentBodyHtml = '';

    // 1. First priority: Check if the invoice or template has saved email content (should not exist for new ones)
    if ((invoice as any).emailTemplate && (invoice as any).emailTemplate.subject && (invoice as any).emailTemplate.body) {
      currentSubject = (invoice as any).emailTemplate.subject;
      currentBodyHtml = (invoice as any).emailTemplate.body;
    }
    // 2. Second priority: For recurring, use only recurringInvoice and recurringInvoiceSubject from company settings
    else if (invoiceType === 'recurring') {
      if (company.emailSettings?.recurringInvoice && company.emailSettings?.recurringInvoiceSubject) {
        currentSubject = company.emailSettings.recurringInvoiceSubject;
        currentBodyHtml = company.emailSettings.recurringInvoice;
      } else {
        // 3. Finally fall back to default template
        currentSubject = defaultTemplates.recurringInvoiceSubject;
        currentBodyHtml = defaultTemplates.recurringInvoice;
      }
    }
    // For standard invoices, use payment request template
    else {
      if (company.emailSettings?.paymentRequest && company.emailSettings?.paymentRequestSubject) {
        currentSubject = company.emailSettings.paymentRequestSubject || `Invoice ${invoice.invoiceNumber} from ${company.name}`;
        currentBodyHtml = company.emailSettings.paymentRequest || '';
      } else {
        currentSubject = `Invoice ${invoice.invoiceNumber} from ${company.name}`;
        currentBodyHtml = defaultTemplates.paymentRequest || '';
      }
    }


    // Ensure the payment link is present in the body
    currentBodyHtml = ensurePaymentLink(currentBodyHtml);

    // Process Subject and Body
    const finalSubject = processSubjectTemplate(currentSubject, invoice, company);
    const finalBody = processEmailTemplate(currentBodyHtml, invoice, company);

    setEmailSubject(finalSubject);
    setEmailBody(finalBody);
    setInitialEmailSubject(finalSubject);
    setInitialEmailBody(finalBody);
  }, [invoice, company, isLoadingCompany, isLoadingInvoice]);
  
  // Compute payment URL
  const paymentDomain = 'https://www.secured-payment-processing.com';
  const paymentUrl = invoice ? `${paymentDomain}/pay/${invoice.id}` : '';
  
  // Function to create a standard invoice from a template
  const createStandardInvoiceFromTemplate = async (
    templateData: any,
    templateId: string,
    templateDueDate: string | Date,
    emailTemplateData?: any
  ) => {
    try {
      // Validate required fields
      if (!templateData.companyId) {
        throw new Error('Template data missing companyId');
      }

      // Get company settings for invoice number generation
      const companySettingsDoc = await getDoc(doc(db, 'companies', templateData.companyId));
      const companySettings = companySettingsDoc.exists() ? companySettingsDoc.data() : null;

      // Get existing invoices for number generation
      const existingInvoicesQuery = query(
        collection(db, 'invoices'),
        where('companyId', '==', templateData.companyId),
        orderBy('invoiceNumber', 'desc'),
        limit(100)
      );
      const existingInvoicesSnapshot = await getDocs(existingInvoicesQuery);
      const existingInvoices = existingInvoicesSnapshot.docs.map(doc => doc.data());

      // Generate new invoice number
      const newInvoiceNumber = generateInvoiceNumber(companySettings, existingInvoices);

      // --- ACH LOGIC START ---
      // Determine payment methods, ensuring ACH is included if enabled in company settings
      let paymentMethods = templateData.paymentMethods || ['credit_card'];
      if (companySettings?.payment?.achEnabled && !paymentMethods.includes('ach')) {
        paymentMethods = [...paymentMethods, 'ach'];
      }
      // --- ACH LOGIC END ---

      // Get customer details to check for company name
      let customerName = templateData.customerName || '';
      const customerId = templateData.customerDetails?.id || templateData.customerId;
      if (customerId) {
        const customerDoc = await getDoc(doc(db, 'customers', customerId));
        if (customerDoc.exists()) {
          const customerData = customerDoc.data();
          // Use company name if available, otherwise fall back to customer name
          customerName = customerData.companyName || `${customerData.firstName} ${customerData.lastName}`.trim() || customerName;
        }
      }

      // Format dates to YYYY-MM-DD
      const formatDateToYYYYMMDD = (date: string | Date): string => {
        const d = typeof date === 'string' ? new Date(date) : date;
        // Add timezone offset to keep the date consistent
        const userTimezoneOffset = d.getTimezoneOffset() * 60000;
        const localDate = new Date(d.getTime() + userTimezoneOffset);
        return `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
      };

      // Get today's date in YYYY-MM-DD format
      const today = formatDateToYYYYMMDD(new Date());

      // Get the due date from the template's schedulePreview.firstPaymentDue
      const dueDate = templateData.schedulePreview?.firstPaymentDue || templateDueDate;
      const formattedDueDate = formatDateToYYYYMMDD(dueDate);


      const invoiceData: any = {
        // Company and customer details
        companyId: templateData.companyId,
        customerId: templateData.customerDetails?.id || templateData.customerId || null,
        customerName,
        customerEmail: templateData.customerEmail || '',
        customerAddress: templateData.customerDetails?.address || templateData.customerAddress || '',
        street: templateData.street || templateData.customerDetails?.street || '',
        city: templateData.city || templateData.customerDetails?.city || '',
        state: templateData.state || templateData.customerDetails?.state || '',
        zip: templateData.zip || templateData.customerDetails?.zip || '',

        // Invoice details
        invoiceNumber: newInvoiceNumber,
        subtotal: templateData.subtotal || templateData.invoiceDetails?.subtotal || 0,
        taxRate: templateData.taxRate || templateData.invoiceDetails?.taxRate || 0,
        taxAmount: templateData.taxAmount || templateData.invoiceDetails?.taxAmount || 0,
        total: templateData.total || templateData.invoiceDetails?.total || 0,
        platformFee: 0,

        // Dates in YYYY-MM-DD format
        date: today,
        dueDate: formattedDueDate,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),

        // Status and type
        status: 'pending',
        invoiceType: 'standard',

        // Items and payment details
        items: templateData.items || templateData.lineItems || [], // Support both items and lineItems
        paymentMethods,
        paymentKey: generatePaymentKey(),
        notes: templateData.notes || templateData.invoiceDetails?.notes || '',

        // Metadata
        metadata: {
          isRecurring: true,
          recurringTemplateId: templateId,
          ...(templateData.metadata || {})
        },

        // Created by
        createdBy: user.email,

        // Activities
        activities: [{
          id: crypto.randomUUID(),
          type: 'created',
          description: 'Invoice created from recurring template',
          timestamp: new Date().toISOString(),
          performedBy: user.email,
          metadata: {
            templateId: templateId
          }
        }]
      };

      // Only include email template if it was modified
      if (emailTemplateData) {
        invoiceData.emailTemplate = emailTemplateData;
      }

      // Create the invoice
      const newInvoiceRef = await addDoc(collection(db, 'invoices'), invoiceData);

      // Add invoice to template tracking
      try {
        await addInvoiceToTemplateTracking(templateId, {
          invoiceId: newInvoiceRef.id,
          invoiceNumber: newInvoiceNumber,
          dueDate: formattedDueDate,
          amount: templateData.total || templateData.invoiceDetails?.total || 0,
          status: 'pending'
        });
      } catch (trackingError) {
        console.error('Error adding invoice to template tracking:', trackingError);
        // Don't fail the invoice creation if tracking fails
      }

      return newInvoiceRef.id;
    } catch (error) {
      console.error('Error in createStandardInvoiceFromTemplate:', error);
      throw error;
    }
  };
  
  // Loading state - also show loading if we don't have the required data for the query yet
  if (isLoadingInvoice || isLoadingCompany || !invoiceId || !user?.companyId) {
    return (
      <div className="flex h-[70vh] items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary/70 mx-auto" />
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">Loading email preview...</p>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Please wait while we prepare your email</p>
        </div>
      </div>
    );
  }
  
  if (!invoice || !company) {
    if (!showError) {
      return (
        <div className="flex h-[70vh] items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary/70 mx-auto" />
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">Loading email preview...</p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Please wait while we prepare your email</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="max-w-3xl mx-auto my-10 p-8 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-200 dark:border-red-900/20 shadow-sm">
        <div className="flex items-center text-red-700 dark:text-red-400 mb-4">
          <AlertCircle className="h-8 w-8 mr-3" />
          <h2 className="text-2xl font-semibold">Invoice Not Found</h2>
        </div>
        <p className="text-gray-700 dark:text-gray-300 mb-6">
          We couldn't find the invoice you're looking for. It may have been deleted or you may not have permission to view it.
        </p>
        <Button 
          onClick={() => navigate('/app/invoices')}
          className="bg-gray-800 hover:bg-gray-700 text-white"
        >
          Return to Invoices
        </Button>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header with progress indicator */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
                {isRecurringInvoice ? "Recurring Invoice Template" : "Email Invoice"}
              </h1>
              <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-900/30">
                Final Step
              </Badge>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {isRecurringInvoice 
                ? "Customize the email template for recurring invoices" 
                : `Customize the email that will be sent to ${invoice.customerName}`}
            </p>
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2 border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/50"
            onClick={handleBack}
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Invoice Preview
          </Button>
        </div>
        
        <div className="relative flex items-center justify-between max-w-3xl mx-auto mb-4">
          <div className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 bg-gray-200 dark:bg-gray-700 -z-10"></div>
          
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
              1
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-400">Create</span>
          </div>
          
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
              2
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-400">Preview</span>
          </div>
          
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary-foreground border-2 border-primary text-primary flex items-center justify-center font-bold shadow-sm">
              3
            </div>
            <span className="text-xs text-gray-900 dark:text-gray-100 font-medium">Send Email</span>
          </div>
          
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 border border-gray-300 dark:border-gray-700 flex items-center justify-center">
              4
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500">Done</span>
          </div>
        </div>
      </div>
      
      <div className="grid md:grid-cols-8 gap-6">
        <div className="md:col-span-5 space-y-6">
          <Card className="p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
            <div className="flex items-center gap-2 mb-6">
              <Mail className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Email Content</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="emailSubject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Subject
                </label>
                <Input
                  id="emailSubject"
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject"
                  className="w-full bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Body
                </label>
                <EmailEditor
                  content={emailBody}
                  onChange={setEmailBody}
                  subject={emailSubject}
                  onSubjectChange={setEmailSubject}
                  companyId={company?.id || company?.companyId || ''}
                  placeholder="Write your invoice email..."
                  paymentUrl={paymentUrl}
                />
              </div>
            </div>
          </Card>
        </div>
        
        <div className="md:col-span-3 space-y-6">
          <Card className="p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 bg-gray-50/70 dark:bg-gray-800/50">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="h-5 w-5 text-gray-700 dark:text-gray-300" />
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                {isRecurringInvoice ? "Template Summary" : "Invoice Summary"}
              </h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {isRecurringInvoice ? "Template #:" : "Invoice #:"}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{invoice.invoiceNumber}</span>
              </div>
              
              <div className="flex justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Customer:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{invoice.customerName}</span>
              </div>
              
              <div className="flex justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">Amount:</span>
                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{formatCurrency((invoice.subtotal || 0) + (invoice.taxAmount || 0))}</span>
              </div>
              
              {isRecurringInvoice && invoice.recurringDetails?.frequency && (
                <div className="flex justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Frequency:</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                    {invoice.recurringDetails.frequency}
                  </span>
                </div>
              )}
              
              <div className="flex justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {isRecurringInvoice ? "First Due Date:" : "Due Date:"}
                </span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {formatDate(invoice.dueDate)}
                </span>
              </div>
              
              {invoice.invoiceType !== 'standard' && (
                <div className="flex justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Type:</span>
                  <Badge variant="outline" className="text-xs font-medium capitalize bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400 border-blue-200 dark:border-blue-800/30">
                    {invoice.invoiceType}
                  </Badge>
                </div>
              )}
            </div>
            
            <div className="mt-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                {isRecurringInvoice ? (
                  <>
                    <FileText className="h-4 w-4 text-primary/70" />
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Template Information</h3>
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 text-primary/70" />
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Recipient</h3>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-md border border-gray-200 dark:border-gray-700">
                <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                    {invoice.customerName?.charAt(0) || 'C'}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {isRecurringInvoice ? (
                      <span className="flex items-center gap-1">
                        {invoice.customerName}
                        <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-200">
                          Template
                        </Badge>
                      </span>
                    ) : (
                      invoice.customerName
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {isRecurringInvoice ? (
                      'This template will be used to generate future invoices'
                    ) : (
                      invoice.customerEmail
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="flex flex-col space-y-4">
            {success ? (
              <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30 rounded-lg p-6 text-center">
                <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-medium text-lg text-green-700 dark:text-green-400">
                  {isRecurringInvoice ? "Template Saved Successfully!" : "Invoice Sent Successfully!"}
                </h3>
                <p className="text-sm text-green-600 dark:text-green-500 mt-2">
                  Redirecting to invoices list...
                </p>
              </div>
            ) : (
              <Button
                className={`w-full py-6 text-base flex items-center gap-3 text-white font-medium shadow-sm ${
                  isRecurringInvoice 
                  ? "bg-green-600 hover:bg-green-700" 
                  : "bg-primary hover:bg-primary/90"
                }`}
                onClick={isRecurringInvoice ? handleSaveRecurringTemplate : handleSendInvoice}
                disabled={sending || !emailSubject || !emailBody || isLoadingInvoice || isLoadingCompany || !user || !invoice}
              >
                {sending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {isRecurringInvoice ? "Saving..." : "Sending..."}
                  </>
                ) : (
                  <>
                    {isRecurringInvoice ? (
                      <>
                        <Save className="h-5 w-5" />
                        Save & Activate Recurring Template
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Send Invoice
                      </>
                    )}
                  </>
                )}
              </Button>
            )}
            
            {/* Add Save as Draft button for non-recurring invoices */}
            {!isRecurringInvoice && (
              <Button
                variant="outline"
                className="w-full py-4 text-base flex items-center gap-3 font-medium border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={handleSaveAsDraft}
                disabled={sending || isLoadingInvoice || isLoadingCompany || !user || !invoice}
              >
                {sending ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    Save as Draft
                  </>
                )}
              </Button>
            )}
            
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              {isRecurringInvoice 
                ? "This is a recurring invoice template. The email content will be saved as a template for future use and will not be sent to the customer."
                : "The invoice will be sent to the customer's email with a payment link."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 